/*
 * label-maker/data.js — variable data for a sheet of labels.
 *
 * A label tool that can only print the same thing 65 times is a toy. The real
 * jobs are lists: 28 children's names for exercise books, 40 jars with a date,
 * 100 inventory tags numbered 001-100, an address list out of a spreadsheet.
 *
 * WHAT GOES WRONG HERE, AND WHAT THIS MODULE REFUSES TO DO ABOUT IT
 * ─────────────────────────────────────────────────────────────────
 *  - A Russian Excel "CSV" is semicolon-separated, not comma-separated,
 *    because the decimal separator is a comma. Assuming `,` turns every row
 *    into one field. Delimiter is therefore SNIFFED, outside quotes.
 *  - Encoding: handled upstream by SubtitleParser.decodeBytes, which this
 *    module deliberately does not duplicate.
 *  - Quoted fields with embedded delimiters and newlines are normal in
 *    address data ("Ленина, 12"). A naive split destroys them silently.
 *  - Trailing blank lines from a text area become blank labels — a whole
 *    row of empty stickers on the sheet. Dropped, but only at the END:
 *    an interior blank line in a pasted list can be a deliberate skip.
 *
 * Pure module: no DOM, no fetch, no browser API. Tested by node.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelData = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var DELIMS = [';', ',', '\t', '|'];

    /* ── list ────────────────────────────────────────────────────────────────
     * One label per line. The single most common input, and the one people
     * reach for by pasting a column out of a spreadsheet. */
    function parseList(text) {
        var lines = String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n');
        while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
        return lines.map(function (l, i) {
            return { '#': String(i + 1), 'value': l.trim(), 'текст': l.trim() };
        });
    }

    /* ── table ───────────────────────────────────────────────────────────────
     * RFC-4180 quoting with a sniffed delimiter. */
    function sniffDelimiter(text) {
        var line = firstLogicalLine(text);
        var best = ',', bestN = -1;
        DELIMS.forEach(function (d) {
            var n = countOutsideQuotes(line, d);
            if (n > bestN) { bestN = n; best = d; }
        });
        return bestN > 0 ? best : null;
    }

    /* The first line that is not inside a quoted field. */
    function firstLogicalLine(text) {
        var s = String(text == null ? '' : text).replace(/\r\n?/g, '\n');
        var q = false;
        for (var i = 0; i < s.length; i++) {
            if (s[i] === '"') {
                if (q && s[i + 1] === '"') { i++; continue; }
                q = !q;
            } else if (s[i] === '\n' && !q) return s.slice(0, i);
        }
        return s;
    }

    function countOutsideQuotes(line, d) {
        var n = 0, q = false;
        for (var i = 0; i < line.length; i++) {
            if (line[i] === '"') {
                if (q && line[i + 1] === '"') { i++; continue; }
                q = !q;
            } else if (line[i] === d && !q) n++;
        }
        return n;
    }

    function splitRows(text, d) {
        var s = String(text == null ? '' : text).replace(/\r\n?/g, '\n');
        var rows = [], row = [], cell = '', q = false;
        for (var i = 0; i < s.length; i++) {
            var c = s[i];
            if (q) {
                if (c === '"') {
                    if (s[i + 1] === '"') { cell += '"'; i++; }
                    else q = false;
                } else cell += c;
                continue;
            }
            if (c === '"') { q = true; continue; }
            if (c === d) { row.push(cell); cell = ''; continue; }
            if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
            cell += c;
        }
        row.push(cell);
        rows.push(row);
        while (rows.length && rows[rows.length - 1].every(function (v) { return v.trim() === ''; })) rows.pop();
        return rows;
    }

    /* A header row is one where every cell is non-empty, none is purely
     * numeric, and no value repeats. Spreadsheets almost always have one;
     * pasted raw data almost never does. When in doubt we say "no header" and
     * synthesise column names, because inventing a header eats a real record. */
    function looksLikeHeader(row, next) {
        if (!row || !row.length) return false;
        var seen = {};
        for (var i = 0; i < row.length; i++) {
            var v = row[i].trim();
            if (!v) return false;
            if (/^-?[\d\s.,]+$/.test(v)) return false;
            var k = v.toLowerCase();
            if (seen[k]) return false;
            seen[k] = 1;
        }
        // If the following row has the same shape AND the same "wordiness",
        // this is probably just data. One column of names would otherwise be
        // beheaded on every import.
        if (next && next.length === row.length && row.length === 1) return false;
        return true;
    }

    function parseTable(text, opts) {
        opts = opts || {};
        var d = opts.delimiter || sniffDelimiter(text);
        if (!d) {
            // No delimiter anywhere: this is a plain list, not a table. Saying
            // so is more useful than returning a one-column table nobody asked
            // for, because the UI can then offer list-shaped controls.
            return { kind: 'list', delimiter: null, fields: ['value'], rows: parseList(text) };
        }
        var raw = splitRows(text, d);
        if (!raw.length) return { kind: 'table', delimiter: d, fields: [], rows: [] };

        var hasHeader = opts.header != null ? !!opts.header : looksLikeHeader(raw[0], raw[1]);
        var fields, body;
        if (hasHeader) {
            fields = raw[0].map(function (h, i) { return h.trim() || ('col' + (i + 1)); });
            body = raw.slice(1);
        } else {
            var width = raw.reduce(function (m, r) { return Math.max(m, r.length); }, 0);
            fields = [];
            for (var i = 0; i < width; i++) fields.push('col' + (i + 1));
            body = raw;
        }
        fields = dedupe(fields);

        var rows = body.map(function (r, idx) {
            var rec = { '#': String(idx + 1) };
            for (var i = 0; i < fields.length; i++) rec[fields[i]] = (r[i] == null ? '' : r[i]).trim();
            // `value` is the implicit "the thing on the label" used by simple
            // presets, so a table stays usable without mapping anything.
            if (rec.value == null) rec.value = rec[fields[0]] || '';
            return rec;
        });
        return { kind: 'table', delimiter: d, header: hasHeader, fields: fields, rows: rows };
    }

    function dedupe(names) {
        var seen = {}, out = [];
        names.forEach(function (n) {
            var base = n, k = n.toLowerCase(), i = 2;
            while (seen[k]) { n = base + '_' + (i++); k = n.toLowerCase(); }
            seen[k] = 1;
            out.push(n);
        });
        return out;
    }

    /* ── sequence ────────────────────────────────────────────────────────────
     * Inventory tags, seat numbers, jar batches. `pad` is what makes 001 sort
     * next to 002 in every system that will ever read the sticker. */
    function sequence(o) {
        o = o || {};
        var count = clamp(int(o.count, 10), 1, 5000);
        var start = int(o.start, 1);
        var step = int(o.step, 1) || 1;
        var pad = clamp(int(o.pad, 0), 0, 12);
        var prefix = str(o.prefix), suffix = str(o.suffix);
        var out = [];
        for (var i = 0; i < count; i++) {
            var n = start + i * step;
            var digits = String(Math.abs(n));
            while (pad && digits.length < pad) digits = '0' + digits;
            var v = prefix + (n < 0 ? '-' : '') + digits + suffix;
            out.push({ '#': String(i + 1), 'value': v, 'номер': v, 'n': String(n) });
        }
        return out;
    }

    /* ── merge ───────────────────────────────────────────────────────────────
     * `{{field}}` is the placeholder. Unknown fields resolve to an empty
     * string rather than staying on the label as literal braces — a sheet that
     * prints "{{имя}}" 40 times is worse than one that prints nothing there,
     * and the UI warns about unknown fields before export. */
    var TOKEN = /\{\{\s*([^}]+?)\s*\}\}/g;

    function merge(template, record, index) {
        if (template == null) return '';
        var rec = record || {};
        return String(template).replace(TOKEN, function (_, name) {
            var key = name.trim();
            if (key === '#') return String((index == null ? 0 : index) + 1);
            if (Object.prototype.hasOwnProperty.call(rec, key)) return String(rec[key] == null ? '' : rec[key]);
            var lower = key.toLowerCase();
            for (var k in rec) {
                if (Object.prototype.hasOwnProperty.call(rec, k) && k.toLowerCase() === lower) {
                    return String(rec[k] == null ? '' : rec[k]);
                }
            }
            return '';
        });
    }

    /* Which `{{tokens}}` a template uses, and which of them no record can fill.
     * Called before export so the user learns about a typo in a field name
     * from us, not from 65 half-empty stickers. */
    function auditTokens(templates, fields) {
        var used = {}, known = {};
        (fields || []).forEach(function (f) { known[String(f).toLowerCase()] = 1; });
        known['#'] = 1;
        [].concat(templates || []).forEach(function (t) {
            String(t == null ? '' : t).replace(TOKEN, function (_, name) {
                used[name.trim()] = 1;
                return '';
            });
        });
        var all = Object.keys(used);
        return {
            used: all,
            unknown: all.filter(function (n) { return !known[n.toLowerCase()]; }),
        };
    }

    /* ── expansion onto the sheet ───────────────────────────────────────────
     * `copies` repeats each record consecutively (3 stickers per child, not
     * three passes of the whole class). `skip` leaves the first N cells of the
     * first sheet blank, which is how a half-used sheet gets finished — the
     * single most requested thing in this segment and the reason people keep a
     * drawer of sheets with four labels missing. */
    function expand(records, opts) {
        opts = opts || {};
        var recs = [].concat(records || []);
        var copies = clamp(int(opts.copies, 1), 1, 1000);
        var skip = Math.max(0, int(opts.skip, 0));
        var out = [];
        for (var i = 0; i < skip; i++) out.push(null);          // blank cells
        for (var r = 0; r < recs.length; r++) {
            for (var c = 0; c < copies; c++) out.push(recs[r]);
        }
        if (opts.total != null) {
            var total = clamp(int(opts.total, out.length), 1, 20000);
            if (opts.repeat && out.length > skip) {
                while (out.length < total) out.push(out[skip + ((out.length - skip) % (out.length - skip))]);
            }
            out.length = Math.min(out.length, total);
        }
        return out;
    }

    /* How many sheets a job needs, and how many cells of the last one are
     * wasted. Shown before export because "this will use 3 sheets and waste
     * 41 labels" changes what people choose to print. */
    function plan(cellCount, perSheet) {
        var n = Math.max(0, int(cellCount, 0));
        var p = Math.max(1, int(perSheet, 1));
        var sheets = Math.ceil(n / p) || 0;
        return { cells: n, perSheet: p, sheets: sheets, wasted: sheets * p - n };
    }

    function int(v, d) { v = parseInt(v, 10); return isFinite(v) ? v : d; }
    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
    function str(v) { return v == null ? '' : String(v); }

    return {
        parseList: parseList,
        parseTable: parseTable,
        sniffDelimiter: sniffDelimiter,
        sequence: sequence,
        merge: merge,
        auditTokens: auditTokens,
        expand: expand,
        plan: plan,
    };
});
